import React, {
    useContext,
    useEffect,
    useRef,
    useState,
    useLayoutEffect,
} from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import { MainContext } from "../../contexts/MainContext";
import { imageService } from "../../services/imageService";

import img1 from "../../static/img/test/image1.png";
import img2 from "../../static/img/test/image2.png";
import img3 from "../../static/img/test/image3.png";

import "./Modal.scss";
import "./Adaptations.scss";

export function CloseBtn({ func }) {
    return (
        <>
            <img
                onClick={func}
                className="close-btn-img"
                src="/static/close_btn.svg"
                alt=""
            />
        </>
    );
}

export default function Modal({ property, onClose }) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isVerySmallDevice, setIsVerySmallDevice] = useState(
        window.innerWidth <= 480
    );
    const [firstGalleryImage, setFirstGalleryImage] = useState(null);
    const [isLoadingGalleryImage, setIsLoadingGalleryImage] = useState(false);

    // Check if device is mobile or very small
    useLayoutEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            setIsMobile(width <= 768);
            setIsVerySmallDevice(width <= 480);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const { cityName, communityName } = useParams();
    const menuRef = useRef(null);
    const location = useLocation();
    const { setModal } = useContext(MainContext);

    // Fetch first gallery image
    useEffect(() => {
        const fetchFirstGalleryImage = async () => {
            if (!property || !property.id) {
                setIsLoadingGalleryImage(false);
                return;
            }

            setIsLoadingGalleryImage(true);

            try {
                const galleryImages = await imageService.getGalleryImages(
                    property.id
                );

                if (galleryImages && galleryImages.length > 0) {
                    // Sort by position and get the first image
                    const sortedImages = [...galleryImages].sort(
                        (a, b) => a.position - b.position
                    );
                    const firstImageUrl = imageService.getImageUrl(
                        sortedImages[0].path
                    );
                    setFirstGalleryImage(firstImageUrl);
                }
            } catch (error) {
                console.error(
                    "Failed to fetch gallery images for preview:",
                    error
                );
                // If fetching fails, keep the default image
            } finally {
                setIsLoadingGalleryImage(false);
            }
        };

        fetchFirstGalleryImage();
    }, [property]);

    // Get propertyName from property prop path
    const propertyName = property?.path ? property.path.split("/").pop() : "";

    const close = () => {
        if (onClose) {
            onClose();
        } else {
            setModal(false);
        }
    };

    // CURRENTLY DON'T NEED IT
    // useEffect(() => {
    //     const handleClickOutside = (event) => {
    //         if (menuRef.current && !menuRef.current.contains(event.target)) {
    //             close();
    //         }
    //     };
    //
    //     document.addEventListener("mousedown", handleClickOutside);
    //     return () => {
    //         document.removeEventListener("mousedown", handleClickOutside);
    //     };
    // }, [menuRef]);

    const getDescription = () => {
        if (!property) return "description is not found";
        return property.desc || "description is not found";
    };

    const getModalAlign = () => {
        return property ? property.modalAlign : "description is not found";
    };

    const getModalTitle = () => {
        return property ? property.short_desc : "modal title is not found";
    };

    const getLink = () => {
        return property ? property.link : "link is not found";
    };

    const getFormattedLink = () => {
        const link = getLink().replace(/^https?:\/\/(www\.)?/, "");
        return link.length > 43 ? link.slice(0, 43) + "..." : link;
    };

    const getPropertyLogo = () => {
        return property ? property.logo : "";
    };

    const getDocs = () => {
        return property ? property.docs : null;
    };

    const getOthers = () => {
        if (!property) return null;

        // Debug output
        console.log("Property:", property.name);
        console.log("Others value:", property.others);

        // Handle both formats - legacy format (object with pdf) and new format (direct string)
        if (
            typeof property.others === "object" &&
            property.others !== null &&
            property.others.pdf
        ) {
            // Legacy format - object with pdf property
            return property.others.pdf;
        } else if (
            typeof property.others === "string" &&
            property.others.trim() !== ""
        ) {
            // New format - direct string
            return property.others;
        }

        return null;
    };

    const fadeVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
        exit: { opacity: 0 },
    };

    return (
        <motion.div
            className="modal-window"
            ref={menuRef}
            style={{
                transform:
                    isMobile || isVerySmallDevice
                        ? "translate(-50%, -50%)" // center on mobile
                        : getModalAlign() === "right"
                        ? "translate(20%, -50%)" // 20% to the right from center on desktop
                        : "translate(-120%, -50%)", // 20% to the left from center on desktop
            }}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={fadeVariants}
            transition={{ duration: 0.3 }}
        >
            <CloseBtn func={close} />
            {getPropertyLogo() ? (
                <img
                    className="modal-window-property-logo"
                    src={getPropertyLogo()}
                    alt="Property Logo"
                />
            ) : (
                <div className="modal-window-property-logo-text">Ellington</div>
            )}
            <span className="modal-title">{getModalTitle()}</span>
            <span className="modal-dcs">{getDescription()}</span>
            <div className="modal-window-main-btns">
                <Link
                    to={`/${cityName}/${communityName}/${propertyName}/gallery`}
                    className="modal-window-btn"
                >
                    {isLoadingGalleryImage ? (
                        <div
                            style={{
                                width: "13.2vh",
                                height: "11.2vh",
                                boxSizing: "border-box",
                                padding: "1vh 1vh 0 1vh",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#f0f0f0",
                            }}
                        >
                            <div className="spinner" />
                        </div>
                    ) : (
                        <img
                            src={firstGalleryImage || img1}
                            alt="s"
                            style={{
                                width: "13.2vh",
                                height: "11.2vh",
                                boxSizing: "border-box",
                                padding: firstGalleryImage ? "1vh 1vh 0 1vh" : "0",
                            }}
                        />
                    )}
                    <span>gallery</span>
                </Link>
                <Link
                    to={`/${cityName}/${communityName}/${propertyName}/videos`}
                    className="modal-window-btn"
                >
                    <img src={img2} alt="a" />
                    <span>videos</span>
                </Link>
                <Link
                    to={`/${cityName}/${communityName}/${propertyName}/floor-plans`}
                    className="modal-window-btn"
                >
                    <img src={img3} alt="s" />
                    <span>floor plans</span>
                </Link>
            </div>
            <div className="modal-window-main-btns2">
                <div className="modal-window-btn2">
                    {getDocs() ? (
                        <Link
                            to={`/${cityName}/${communityName}/${propertyName}/documents`}
                        >
                            documents
                        </Link>
                    ) : (
                        <span className="disabled-link">documents</span>
                    )}
                </div>
                <div className="modal-window-btn2">
                    {getOthers() && getOthers().trim() !== "" ? (
                        <Link
                            to={`/${cityName}/${communityName}/${propertyName}/others`}
                        >
                            others
                        </Link>
                    ) : (
                        <span className="disabled-link">others</span>
                    )}
                </div>
            </div>
            <a target="_blank" className="web-link" href={getLink()}>
                {/* {getFormattedLink()} */}
                Open website
            </a>
        </motion.div>
    );
}
